import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useCurrentAccess } from "../../redux/api/org-user/orgUserAPI";
import {
  AccessUnavailableState,
  RoleRestrictionState,
} from "../../shared/components/access-state-card/access-states";
import { IHttpError } from "../../shared/oversight-core/interfaces/http-errror";
import SettingItem from "../../shared/oversight-core/shared-components/setting-item/setting-item";
import DataTable from "../../shared/oversight-core/ui-elements/data-table/data-table";
import MaterialIcon from "../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  canManageUsers,
  getAccessLevelInfo,
  getRoleLabel,
  grantableAccessLevels,
  orgUserRoleLabels,
} from "../../shared/oversight-core/utils/get-org-user-info";
import SettingsTableCard from "./components/settings-table-card/settings-table-card";
import SiteClassification from "./components/site-classification/site-classification";
import {
  accessCapabilities,
  CapabilityValue,
  roleDefinitions,
} from "./settings-helpers";
import styles from "./settings.module.scss";

enum ESettingsTab {
  ROLES = 1,
  ACCESS_LEVELS = 2,
  SITE_CLASSIFICATION = 3,
}

const tabTitles: Record<ESettingsTab, string> = {
  [ESettingsTab.ROLES]: "Roles",
  [ESettingsTab.ACCESS_LEVELS]: "Access levels",
  [ESettingsTab.SITE_CLASSIFICATION]: "Site classification",
};

const CapabilityCell = (props: { value: CapabilityValue }) => {
  const { value } = props;

  return (
    <div className="d-flex justify-content-center">
      {value === true ? (
        <MaterialIcon icon="check" size={18} color="#29cc39" />
      ) : value === false ? (
        <span className={styles.notAllowed}>—</span>
      ) : (
        <span className={`${styles.condition} font-size-13 font-weight-500`}>
          {value}
        </span>
      )}
    </div>
  );
};

const AdminSettings = () => {
  const [selectedTab, setSelectedTab] = useState(ESettingsTab.ROLES);
  const {
    data: currentAccess,
    isLoading: isLoadingAccess,
    error: accessError,
  } = useCurrentAccess();

  if (isLoadingAccess) {
    return <SpinnerModal show />;
  }

  // Fail closed: without a loaded access record the role can't be checked.
  if (!currentAccess) {
    return (
      <AccessUnavailableState message={(accessError as IHttpError)?.message} />
    );
  }

  if (!canManageUsers(currentAccess)) {
    return (
      <RoleRestrictionState roleLabel={getRoleLabel(currentAccess.role)} />
    );
  }

  return (
    <div className={`${styles.container} px-2 px-lg-4`}>
      <Row>
        <Col className={`col-12 col-lg-3 col-xl-2 ${styles.navContainer}`}>
          <div className={styles.navItems}>
            <SettingItem
              items={[
                { itemName: "Roles", id: ESettingsTab.ROLES },
                { itemName: "Access levels", id: ESettingsTab.ACCESS_LEVELS },
                {
                  itemName: "Site classification",
                  id: ESettingsTab.SITE_CLASSIFICATION,
                },
              ]}
              onChange={(id) => setSelectedTab(id)}
              selectedItemId={selectedTab}
            />
          </div>
        </Col>
        <Col className="col-12 col-lg">
          <div className={styles.content}>
            <div className={`${styles.pageTitle} text-dark font-size-22`}>
              {tabTitles[selectedTab]}
            </div>
            {selectedTab === ESettingsTab.SITE_CLASSIFICATION ? (
              <SiteClassification
                tenantId={
                  currentAccess.platformAdmin
                    ? undefined
                    : currentAccess.tenantId
                }
              />
            ) : selectedTab === ESettingsTab.ROLES ? (
              <>
                <SettingsTableCard>
                  <DataTable
                    headers={[
                      { id: 1, columnName: "Role" },
                      { id: 2, columnName: "Typical access" },
                      { id: 3, columnName: "Seat" },
                    ]}
                    data={roleDefinitions.map((definition) => ({
                      data: [
                        <div
                          key="role"
                          className="text-dark font-size-13 font-weight-600"
                        >
                          {orgUserRoleLabels[definition.role]}
                        </div>,
                        <div key="access" className="font-size-13">
                          {definition.typicalAccess}
                        </div>,
                        <div key="seat" className="font-size-13">
                          {definition.typicalSeat}
                        </div>,
                      ],
                    }))}
                  />
                </SettingsTableCard>
              </>
            ) : (
              <>
                <SettingsTableCard>
                  <DataTable
                    headers={[
                      { id: 1, columnName: "Within the granted area" },
                      ...grantableAccessLevels.map((level, index) => ({
                        id: index + 2,
                        columnName: (
                          <div className="text-center text-nowrap">
                            {getAccessLevelInfo(level).label}
                          </div>
                        ),
                      })),
                    ]}
                    data={accessCapabilities.map((capability) => ({
                      data: [
                        <div
                          key="capability"
                          className="text-dark font-size-13 text-nowrap"
                        >
                          {capability.label}
                        </div>,
                        ...grantableAccessLevels.map((level) => (
                          <CapabilityCell
                            key={level}
                            value={
                              capability.levels[
                                level as keyof typeof capability.levels
                              ]
                            }
                          />
                        )),
                      ],
                    }))}
                  />
                </SettingsTableCard>
              </>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AdminSettings;
